<template>
    <div class="echarts-container" :id="id" />
</template>

<script>
import { debounce } from '@/utils'
import * as echarts from 'echarts'
import { mapGetters } from 'vuex'
export default {
    name: 'EchartsBar',
    props: {
        renderData: {
            type: Object,
            default: () => {}
        },
        id: {
            type: String,
            default: 'pie'
        },
        chartStyle: {
            type: Object,
            default: () => {}
        },
        filterDay: {
            type: Number,
            default: 0
        }
    },
    watch: {
        renderData(val) {
            if (!val) return
            this.initChart()
        },
        getCollapse() {
            clearTimeout(this.timer)
            this.timer = setTimeout(() => {
                if (this.echartsDom) this.echartsDom.resize()
            }, 350)
        }
    },
    computed: {
        ...mapGetters('common', {
            getCollapse: 'getCollapse'
        })
    },
    mounted() {
        if (!this.renderData) return
        this.initChart()
        this.__resizeHandler = debounce(() => {
            if (this.echartsDom) this.echartsDom.resize()
        }, 350)
        window.addEventListener('resize', this.__resizeHandler)
    },
    data() {
        return {
            echartsDom: null,
            selectedBlock: [],
            selectedName: []
        }
    },
    beforeDestroy() {
        if (!this.echartsDom) {
            return
        }
        clearTimeout(this.timer)
        window.removeEventListener('resize', this.__resizeHandler)
        this.echartsDom.dispose()
        this.echartsDom = null
    },
    methods: {
        initChart() {
            this.echartsDom = echarts.getInstanceByDom(document.getElementById(this.id))
            if (this.echartsDom == null) {
                this.echartsDom = echarts.init(document.getElementById(this.id), null, { renderer: 'svg' })
            }
            const { legends, xData, dataList } = this.renderData
            const { grid, legend, xAxis, ser } = this.chartStyle
            let series = []
            if (!dataList) return
            if (dataList.length > 0 && dataList[0] instanceof Array) {
                dataList.forEach((item, index) => {
                    series.push({
                        name: legends[index],
                        type: 'bar',
                        barWidth: ser.barWidth,
                        label: {
                            show: ser.label.show,
                            position: ser.label.position,
                            color: ser.label.color,
                            fontFamily: ser.label.fontFamily,
                            fontWeight: ser.label.fontWeight,
                            fontSize: ser.label.fontSize
                        },
                        data: item.map((v) => ({ selected: false, ...v }))
                    })
                })
            } else {
                series = [
                    {
                        colorBy: 'data',
                        name: legends && legends[0] ? legends[0] : '',
                        type: 'bar',
                        barWidth: ser.barWidth,
                        label: {
                            show: ser.label.show,
                            position: ser.label.position,
                            color: ser.label.color,
                            fontFamily: ser.label.fontFamily,
                            fontWeight: ser.label.fontWeight,
                            fontSize: ser.label.fontSize
                        },
                        data: dataList.map((v) => ({ selected: false, ...v }))
                    }
                ]
            }
            const _this = this
            let option = {
                color: ['#00338d','#00b8f5'],
                grid: {
                    left: grid.left,
                    right: grid.right,
                    top: grid.top,
                    bottom: grid.bottom,
                    containLabel: true
                },
                legend: {
                    show: legend.show,
                    x: 'center',
                    y: 'bottom',
                    icon: 'circle',
                    itemWidth: 8,
                    data: legends,
                    textStyle: {
                        fontSize: this.$i18n.locale === 'zh' ? 11 : 10,
                        lineHeight: this.$i18n.locale === 'zh' ? 11 : 10
                    }
                },
                tooltip: {
                    show: true,
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: function (params) {
                        switch (_this.id) {
                            case 'confirmation-bar3':
                                let str3 = ''
                                for (let item of params) {
                                    str3 += item.name + ' : ' + item.value + '%<br>'
                                }
                                return str3
                            case 'confirmation-bar1':
                                let str = params[0].name + '<br>'
                                for (let item of params) {
                                    str += item.marker + item.seriesName + ' : ' + item.value + '<br>'
                                }
                                return str
                            default:
                                return params[0] ? params[0].value : ''
                        }

                        
                    }
                },
                xAxis: [
                    {
                        type: 'category',
                        axisTick: {
                            show: false
                        },
                        axisLabel: {
                            show: true,
                            interval: 0,
                            width: xAxis.axisLabel.width,
                            overflow: 'break',
                            fontSize: xAxis.axisLabel.fontSize,
                            color: '#333333'
                        },
                        axisLine: {
                            lineStyle: {
                                color: 'rgba(22, 36, 59, 0.05)'
                            }
                        },
                        data: xData
                    }
                ],
                yAxis: [
                    {
                        type: 'value',
                        splitLine: {
                            lineStyle: {
                                color: ['rgba(22, 36, 59, 0.05)']
                            }
                        },
                        axisLabel: {
                            show: true,
                            color: 'rgba(22, 36, 59, 0.4)',
                            formatter: this.id == 'confirmation-bar3' ? '{value}%' : '{value}'
                        }
                    }
                ],
                series: series.map((v) => ({
                    selectedMode: this.id == 'confirmation-bar3' ? false : 'single',
                    select: {
                        itemStyle: {
                            borderColor: '#000',
                            borderWidth: 4
                        }
                    },
                    itemStyle: {
                        normal: {
                            label: {
                                show: true,
                                position: 'top',
                                formatter: this.id == 'confirmation-bar3' ? '{c}%' : '{c}'
                            }
                        }
                    },
                    ...v
                }))
            }
            //  
            this.echartsDom.setOption(option, true)

            /**
             * 点击的时候把别的图的选择状态都清掉
             * 再把参数整理好统一传到列表的筛选条件
             */
            this.echartsDom.on('click', (e) => {

                const options = this.echartsDom.getOption()
                 
                switch (this.id) {
                    // 进度分析
                    case 'confirmation-bar1':
                        {
                            const options = this.echartsDom.getOption()
                            window.__confirmationCleanSelec_bar1 && window.__confirmationCleanSelec_bar1()
                            options.series.forEach((v) => {
                                if (v.name == e.seriesName) {
                                    v.selectedMap[e.name] = true
                                } else {
                                    v.selectedMap = {}
                                    v.selectedMap[e.name] = false
                                }
                            })
                             
                            this.echartsDom.setOption(options, true)

                            // 再清除其它的
                            window.__confirmationCleanSelec_bar2 && window.__confirmationCleanSelec_bar2()
                            window.__confirmationCleanSelec_bar3 && window.__confirmationCleanSelec_bar3()
                            window.__confirmationCleanSelec_pie && window.__confirmationCleanSelec_pie()
                            window.__confirmationCleanSelec_xbar && window.__confirmationCleanSelec_xbar()

                            let type = []
                            let status = []
                            options.series.forEach((v) => {
                                if (
                                    v.selectedMap &&
                                    v.selectedMap[this.$t('confirmation_status._1')] &&
                                    v.selectedMap[this.$t('confirmation_status._1')] === true
                                ) {
                                    status = ['1']
                                    if (v.name == this.$t('confirmation_type._1_all')) {
                                        type = ['1', '2']
                                    } else {
                                        type = ['0']
                                    }
                                }
                                if (
                                    v.selectedMap &&
                                    v.selectedMap[this.$t('confirmation_status._2')] &&
                                    v.selectedMap[this.$t('confirmation_status._2')] === true
                                ) {
                                    status = ['2']
                                    if (v.name == this.$t('confirmation_type._1_all')) {
                                        type = ['1', '2']
                                    } else {
                                        type = ['0']
                                    }
                                }
                                if (
                                    v.selectedMap &&
                                    v.selectedMap[this.$t('confirmation_status._3_3')] &&
                                    v.selectedMap[this.$t('confirmation_status._3_3')] === true
                                ) {
                                    status = ['3']
                                    if (v.name == this.$t('confirmation_type._1_all')) {
                                        type = ['1', '2']
                                    } else {
                                        type = ['0']
                                    }
                                }
                                if (
                                    v.selectedMap &&
                                    v.selectedMap[this.$t('confirmation_status._4')] &&
                                    v.selectedMap[this.$t('confirmation_status._4')] === true
                                ) {
                                    status = ['4']
                                    if (v.name == this.$t('confirmation_type._1_all')) {
                                        type = ['1', '2']
                                    } else {
                                        type = ['0']
                                    }
                                }
                                if (
                                    v.selectedMap &&
                                    v.selectedMap[this.$t('confirmation_status._5')] &&
                                    v.selectedMap[this.$t('confirmation_status._5')] === true
                                ) {
                                    status = ['5']
                                    if (v.name == this.$t('confirmation_type._1_all')) {
                                        type = ['1', '2']
                                    } else {
                                        type = ['0']
                                    }
                                }
                                if (
                                    v.selectedMap &&
                                    v.selectedMap[this.$t('confirmation_status._6')] &&
                                    v.selectedMap[this.$t('confirmation_status._6')] === true
                                ) {
                                    status = ['6']
                                    if (v.name == this.$t('confirmation_type._1_all')) {
                                        type = ['1', '2']
                                    } else {
                                        type = ['0']
                                    }
                                }
                            })

                            if (status.length > 0 || type.length > 0) {
                                window.__listenDashboardChangeFilter && window.__listenDashboardChangeFilter({ type, status })
                                this.$message.success(this.$t('confirmation_dashboard_filtered'))
                            }
                        }
                        break
                    // 待被审计单位签字盖章的函证分析
                    case 'confirmation-bar2':
                        {
                            window.__confirmationCleanSelec_bar1 && window.__confirmationCleanSelec_bar1()
                            window.__confirmationCleanSelec_bar3 && window.__confirmationCleanSelec_bar3()
                            window.__confirmationCleanSelec_pie && window.__confirmationCleanSelec_pie()
                            window.__confirmationCleanSelec_xbar && window.__confirmationCleanSelec_xbar()

                            let type = []
                            const selectedMap = options.series[0].selectedMap
                            if (selectedMap && selectedMap['非银行类函证'] && selectedMap['非银行类函证'] === true) {
                                type = ['0']
                            }
                            if (selectedMap && selectedMap['银行函证'] && selectedMap['银行函证'] === true) {
                                type = ['1', '2']
                            }

                            if (type.length > 0) {
                                const filterDay = this.filterDay
                                window.__listenDashboardChangeFilter && window.__listenDashboardChangeFilter({ type, filterDay })
                                this.$message.success(this.$t('confirmation_dashboard_filtered'))
                            }
                        }
                        break
                    // 回函率分析
                    // case 'confirmation-bar3': {
                    //     window.__confirmationCleanSelec_bar1 && window.__confirmationCleanSelec_bar1()
                    //     window.__confirmationCleanSelec_bar2 && window.__confirmationCleanSelec_bar2()
                    //     window.__confirmationCleanSelec_pie && window.__confirmationCleanSelec_pie()
                    //     window.__confirmationCleanSelec_xbar && window.__confirmationCleanSelec_xbar()

                    //     let type = []
                    //     const selectedMap = options.series[0].selectedMap
                    //     if (selectedMap && selectedMap['非银行函证'] && selectedMap['非银行函证'] === true) {
                    //         type = ['0']
                    //     }
                    //     if (selectedMap && selectedMap['银行函证'] && selectedMap['银行函证'] === true) {
                    //         type = ['1']
                    //     }

                    //     if (type.length > 0) {
                    //         window.__listenDashboardChangeFilter && window.__listenDashboardChangeFilter({type})
                    //         this.$message.success(this.$t('confirmation_dashboard_filtered'))
                    //     }
                    // } break
                }
            })

            // 为柱状图绑定清除方法
            switch (this.id) {
                // 进度分析
                case 'confirmation-bar1':
                    {
                        window.__confirmationCleanSelec_bar1 = (e) => {
                            const options = this.echartsDom.getOption()
                            //  

                            options.series.forEach((v, k) => {
                                v.selectedMap
                                    ? Object.keys(v.selectedMap).forEach((kk) => {
                                          if (v.selectedMap[kk]) {
                                              v.selectedMap[kk] = false
                                          }
                                      })
                                    : ''
                            })

                            //  
                            this.echartsDom.setOption(options, true)
                        }
                    }
                    break
                // 待被审计单位签字盖章的函证分析
                case 'confirmation-bar2':
                    {
                        window.__confirmationCleanSelec_bar2 = (e) => {
                            const options = this.echartsDom.getOption()
                            //  

                            options.series.forEach((v, k) => {
                                v.selectedMap
                                    ? Object.keys(v.selectedMap).forEach((kk) => {
                                          if (v.selectedMap[kk]) {
                                              v.selectedMap[kk] = false
                                          }
                                      })
                                    : ''
                            })

                            //  
                            this.echartsDom.setOption(options, true)
                        }
                    }
                    break
                // 回函率分析
                case 'confirmation-bar3':
                    {
                        window.__confirmationCleanSelec_bar3 = (e) => {
                            const options = this.echartsDom.getOption()
                            //  

                            options.series.forEach((v, k) => {
                                v.selectedMap
                                    ? Object.keys(v.selectedMap).forEach((kk) => {
                                          if (v.selectedMap[kk]) {
                                              v.selectedMap[kk] = false
                                          }
                                      })
                                    : ''
                            })

                            //  
                            this.echartsDom.setOption(options, true)
                        }
                    }
                    break
            }
        }
    }
}
</script>

<style scoped>
.echarts-container {
    width: 100%;
    height: 100%;
}
</style>
